import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuthApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/layout/PageHeader';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/components/ui/use-toast';
import demoStudioService from '@/lib/api/demoStudio';
import { getBrowserTimezone } from '@/lib/timezone';
import {
  Clapperboard,
  Loader2,
  Lock,
  MonitorPlay,
  Sparkles,
} from 'lucide-react';

const DemoStudioPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const { userData } = useAuth();
  const { toast } = useToast();
  const [launching, setLaunching] = React.useState(false);
  const [initialUrl, setInitialUrl] = React.useState('');

  const handleLaunch = React.useCallback(async () => {
    if (!currentOrganization?.id) {
      toast({
        title: 'No organization selected',
        description: 'Select an organization before launching Demo Studio.',
        variant: 'destructive',
      });
      return;
    }
    if (!initialUrl.trim()) {
      toast({
        title: 'Starting page required',
        description: 'Enter the page you want Demo Studio to open first.',
        variant: 'destructive',
      });
      return;
    }

    setLaunching(true);
    try {
      const response = await demoStudioService.createSession({
        organization_id: currentOrganization.id,
        title: `${currentOrganization.name} demo`,
        timezone: userData?.timezone || getBrowserTimezone(),
        initial_url: initialUrl.trim(),
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to launch Demo Studio.');
      }

      toast({
        title: 'Demo Studio launched',
        description: 'Your managed browser session is ready.',
      });
      navigate(`/demo-studio/sessions/${response.data.id}`);
    } catch (error) {
      toast({
        title: 'Launch failed',
        description: error instanceof Error ? error.message : 'Failed to launch Demo Studio.',
        variant: 'destructive',
      });
    } finally {
      setLaunching(false);
    }
  }, [
    currentOrganization?.id,
    currentOrganization?.name,
    initialUrl,
    navigate,
    toast,
    userData?.timezone,
  ]);

  return (
    <>
      <Helmet>
        <title>Demo Studio | Hobbes</title>
      </Helmet>

      <div className="flex min-h-0 flex-col">
        <PageHeader
          title="Demo Studio"
          description="Record product demos inside a managed Hobbes workspace."
          metadata={
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="gap-1.5">
                <Sparkles className="h-3 w-3" />
                New workspace
              </Badge>
              {currentOrganization?.name && (
                <Badge variant="outline">
                  {currentOrganization.name}
                </Badge>
              )}
            </div>
          }
        />

        <div className="flex-1 px-4 pb-8 pt-6 sm:px-6 md:px-8">
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.5fr)_minmax(320px,0.9fr)]">
            <Card className="border card-border shadow-none">
              <CardHeader className="space-y-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <MonitorPlay className="h-6 w-6" />
                </div>
                <div className="space-y-2">
                  <CardTitle className="text-xl">Managed demo recording</CardTitle>
                  <CardDescription className="max-w-2xl text-sm leading-6">
                    Launch a browser-backed recording session, log into the product inside that
                    workspace, and capture the demo from a managed environment.
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="demo-studio-initial-url">Starting page</Label>
                  <Input
                    id="demo-studio-initial-url"
                    type="url"
                    placeholder="https://app.example.com/login"
                    value={initialUrl}
                    onChange={(event) => setInitialUrl(event.target.value)}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <p className="text-sm text-muted-foreground">
                    Demo Studio will open this page first so you can begin recording in the right flow.
                  </p>
                </div>

                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-xl border border-border/70 bg-muted/30 p-4">
                    <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
                      <Clapperboard className="h-4 w-4" />
                      Recording
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Capture the live browser session and narration from a controlled environment.
                    </p>
                  </div>
                  <div className="rounded-xl border border-border/70 bg-muted/30 p-4">
                    <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
                      <MonitorPlay className="h-4 w-4" />
                      Browser infra
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Spin up a managed browser session through the new demo-studio backend.
                    </p>
                  </div>
                  <div className="rounded-xl border border-border/70 bg-muted/30 p-4">
                    <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
                      <Lock className="h-4 w-4" />
                      Controlled access
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Keep recording sessions scoped to the active organization and user.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    onClick={handleLaunch}
                    disabled={launching || !currentOrganization?.id}
                    className="gap-2"
                  >
                    {launching ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Launching...
                      </>
                    ) : (
                      <>
                        <MonitorPlay className="h-4 w-4" />
                        Launch Studio
                      </>
                    )}
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Launch opens a dedicated studio workspace under the Hobbes domain.
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="border card-border shadow-none">
              <CardHeader>
                <CardTitle className="text-base">Workspace direction</CardTitle>
                <CardDescription>
                  Demo Studio should feel like an owned recording environment, not an embed.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
                  1. Launch from here into a dedicated session workspace route.
                </div>
                <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
                  2. Keep the browser full-bleed enough for real demo recording.
                </div>
                <div className="rounded-xl border border-border/70 bg-muted/20 p-4">
                  3. Layer future chat, navigation, and recording controls into the studio shell.
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default DemoStudioPage;
